/**
 * The dashboard greeting, as a pure function of a moment (punch 4.6).
 *
 * BROWSER-LOCAL, deliberately. The server's clock is in UTC and a property has
 * its own `timeZone`, and neither is the answer: this greets the PERSON, so the
 * only honest clock is the one on the device they are reading it on. A
 * coordinator in Charlotte at 8am should not be told "good evening" because the
 * container is in UTC.
 *
 * A function rather than logic inside the component, so the three boundaries
 * are testable without mocking a clock or a render.
 */

export type Greeting = 'Good morning' | 'Good afternoon' | 'Good evening';

/**
 * 00:00–11:59 morning · 12:00–16:59 afternoon · 17:00–23:59 evening.
 *
 * No "Good night" and no small-hours special case: somebody working at 2am is
 * working, and a greeting that remarks on it is the app having an opinion about
 * their hours (Matt, 2026-08-31, approving the boundaries as proposed).
 */
export function greetingFor(now: Date): Greeting {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * "Monday, August 31" — weekday, month, day, in the app's en-US convention and
 * in the viewer's own time zone. No year: the reader knows what year it is, and
 * the line is orientation rather than a date field.
 */
export function bannerDate(now: Date): string {
  return now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}
